import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, ChevronRight, Plus, Mic, X, Pencil, Trash2, FileDown, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CustomerNotesPage = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  
  const [customer, setCustomer] = useState(null);
  const [horses, setHorses] = useState([]);
  const [notesCounts, setNotesCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  
  // Add/Edit horse modal
  const [showAddHorse, setShowAddHorse] = useState(false);
  const [editingHorse, setEditingHorse] = useState(null);
  const [horseForm, setHorseForm] = useState({
    name: '',
    age: '',
    father: '',
    mother: ''
  });

  useEffect(() => {
    fetchData();
  }, [customerId]);

  const fetchData = async () => {
    try {
      // First try to fetch customer from note_customers
      let foundCustomer = null;
      try {
        const noteCustomersRes = await axios.get(`${API}/note-customers`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        foundCustomer = noteCustomersRes.data.find(c => c.id === customerId);
      } catch (e) {
        // Ignore - try jcp_customers next
      }
      
      // Fallback to JCP customers if not found in note_customers
      if (!foundCustomer) {
        const customersRes = await axios.get(`${API}/admin/jcp-customers`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        foundCustomer = customersRes.data.find(c => c.id === customerId);
      }
      setCustomer(foundCustomer);

      // Fetch horses from note_horses (NOT jcp_horses)
      const horsesRes = await axios.get(`${API}/note-horses/customer/${customerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHorses(horsesRes.data);

      // Fetch notes counts per horse
      const notesRes = await axios.get(`${API}/lesson-notes/customer/${customerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Count notes per horse
      const counts = {};
      notesRes.data.forEach(note => {
        const horseId = note.horse_id || 'unassigned';
        counts[horseId] = (counts[horseId] || 0) + 1;
      });
      setNotesCounts(counts);

    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Kunne ikke hente data');
    } finally {
      setLoading(false);
    }
  };

  const openAddHorse = () => {
    setEditingHorse(null);
    setHorseForm({ name: '', age: '', father: '', mother: '' });
    setShowAddHorse(true);
  };

  const openEditHorse = (horse, e) => {
    e.stopPropagation();
    setEditingHorse(horse);
    setHorseForm({
      name: horse.name || '',
      age: horse.age || '',
      father: horse.father || '',
      mother: horse.mother || ''
    });
    setShowAddHorse(true);
  };

  const closeModal = () => {
    setShowAddHorse(false);
    setEditingHorse(null);
    setHorseForm({ name: '', age: '', father: '', mother: '' });
  };

  const saveHorse = async () => {
    if (!horseForm.name.trim()) {
      toast.error('Navn er påkrævet');
      return;
    }

    try {
      const data = {
        customer_id: customerId,
        name: horseForm.name.trim(),
        age: horseForm.age ? parseInt(horseForm.age) : null,
        father: horseForm.father.trim() || null,
        mother: horseForm.mother.trim() || null
      };

      if (editingHorse) {
        await axios.put(`${API}/note-horses/${editingHorse.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Hest opdateret');
      } else {
        await axios.post(`${API}/note-horses`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Hest tilføjet');
      }

      closeModal();
      fetchData();
    } catch (error) {
      console.error('Error saving horse:', error);
      toast.error('Kunne ikke gemme hest');
    }
  };

  const deleteHorse = async (horse, e) => {
    e.stopPropagation();
    if (!window.confirm(`Slet ${horse.name}? Alle noter for denne hest vil også blive slettet.`)) return;

    try {
      await axios.delete(`${API}/note-horses/${horse.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Hest slettet');
      fetchData();
    } catch (error) {
      console.error('Error deleting horse:', error);
      toast.error('Kunne ikke slette hest');
    }
  };

  const exportToPDF = async () => {
    setIsExporting(true);
    try {
      const response = await axios.get(`${API}/lesson-notes/customer/${customerId}/export-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const customerName = customer ? `${customer.first_name}_${customer.last_name}` : 'kunde';
      link.setAttribute('download', `noter_${customerName}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF eksporteret');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Kunne ikke eksportere PDF');
    } finally {
      setIsExporting(false);
    }
  };

  const startQuickRecording = (horse, e) => {
    e.stopPropagation();
    // Navigate to horse notes page with autostart parameter
    navigate(`/admin/noter/${customerId}/hest/${horse.id}?autostart=true`);
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#f5f3f0', padding: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div>Indlæser...</div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f5f3f0', padding: '1rem' }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem' }}>
            <button
              onClick={() => navigate('/admin/noter')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#666', display: 'flex', alignItems: 'center' }}
            >
              <ArrowLeft size={24} />
            </button>
            <div style={{ flex: 1 }}>
              <h1 style={{ margin: 0, fontSize: '1.25rem', color: '#333' }}>
                👤 {customer ? `${customer.first_name} ${customer.last_name}` : 'Kunde'}
              </h1>
              <p style={{ margin: 0, color: '#666', fontSize: '0.85rem' }}>
                {horses.length} {horses.length === 1 ? 'hest' : 'heste'}
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button
              onClick={exportToPDF}
              disabled={isExporting}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.6rem 1rem',
                background: isExporting ? '#ccc' : '#e3f2fd',
                color: isExporting ? '#666' : '#1976d2',
                border: 'none',
                borderRadius: '8px',
                cursor: isExporting ? 'not-allowed' : 'pointer',
                fontWeight: 500,
                fontSize: '0.9rem'
              }}
            >
              {isExporting ? <Loader2 size={16} className="animate-spin" style={{ animation: 'spin 1s linear infinite' }} /> : <FileDown size={16} />}
              {isExporting ? 'Eksporterer...' : 'Eksporter PDF'}
            </button>
            <button
              onClick={openAddHorse}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.6rem 1rem',
                background: '#6b5e8e',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 500,
                fontSize: '0.9rem'
              }}
            >
              <Plus size={16} />
              Tilføj hest
            </button>
          </div>
        </div>

        {/* Horses List */}
        {horses.length === 0 ? (
          <div style={{ 
            background: 'white', 
            borderRadius: '12px', 
            padding: '3rem', 
            textAlign: 'center', 
            color: '#666',
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🐴</div>
            <p style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>Ingen heste endnu</p>
            <p style={{ fontSize: '0.9rem', marginBottom: '1.5rem' }}>
              Tilføj en hest for at begynde at oprette undervisningsnoter
            </p>
            <button
              onClick={openAddHorse}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#6b5e8e',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 500,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <Plus size={18} />
              Tilføj første hest
            </button>
          </div>
        ) : (
          <div style={{ 
            background: 'white', 
            borderRadius: '12px', 
            overflow: 'hidden',
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
          }}>
            {horses.map((horse, index) => {
              const notesCount = notesCounts[horse.id] || 0;
              return (
                <div
                  key={horse.id}
                  onClick={() => navigate(`/admin/noter/${customerId}/hest/${horse.id}`)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '1rem',
                    cursor: 'pointer',
                    borderBottom: index < horses.length - 1 ? '1px solid #f0f0f0' : 'none',
                    transition: 'background 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.background = '#f9f9f9'}
                  onMouseOut={(e) => e.currentTarget.style.background = 'white'}
                >
                  <div style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '10px',
                    background: 'linear-gradient(135deg, #6b5e8e 0%, #8a7aad 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '1rem',
                    fontSize: '1.5rem'
                  }}>
                    🐴
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: '#333', fontSize: '1.1rem' }}>
                      {horse.name}
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.25rem' }}>
                      {[
                        horse.age && `${horse.age} år`,
                        horse.father && `Far: ${horse.father}`,
                        horse.mother && `Mor: ${horse.mother}`
                      ].filter(Boolean).join(' • ') || 'Ingen detaljer'}
                    </div>
                  </div>
                  
                  {/* Action buttons */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {notesCount > 0 && (
                      <div style={{
                        background: '#e8e4f0',
                        color: '#6b5e8e',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '20px',
                        fontSize: '0.85rem',
                        fontWeight: 500
                      }}>
                        {notesCount} {notesCount === 1 ? 'note' : 'noter'}
                      </div>
                    )}
                    
                    {/* Quick record button */}
                    <button
                      onClick={(e) => startQuickRecording(horse, e)}
                      title="Start optagelse"
                      style={{
                        background: '#c62828',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '0.5rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Mic size={18} />
                    </button>
                    
                    {/* Edit button */}
                    <button
                      onClick={(e) => openEditHorse(horse, e)}
                      title="Rediger hest"
                      style={{
                        background: '#fff3e0',
                        color: '#f57c00',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '0.5rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Pencil size={18} />
                    </button>
                    
                    {/* Delete button */}
                    <button
                      onClick={(e) => deleteHorse(horse, e)}
                      title="Slet hest"
                      style={{
                        background: '#ffebee',
                        color: '#c62828',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '0.5rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Trash2 size={18} />
                    </button>
                    
                    <ChevronRight size={20} color="#ccc" />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Add/Edit Horse Modal */}
        {showAddHorse && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '1.5rem',
              width: '100%',
              maxWidth: '400px',
              maxHeight: '90vh',
              overflow: 'auto'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 style={{ margin: 0, fontSize: '1.25rem' }}>
                  {editingHorse ? 'Rediger hest' : 'Tilføj ny hest'}
                </h2>
                <button
                  onClick={closeModal}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#666' }}
                >
                  <X size={24} />
                </button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {/* Name - Required */}
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, color: '#333' }}>
                    Navn *
                  </label>
                  <input
                    type="text"
                    value={horseForm.name}
                    onChange={(e) => setHorseForm({ ...horseForm, name: e.target.value })}
                    placeholder="F.eks. Thunder"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                {/* Age - Optional */}
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, color: '#333' }}>
                    Alder (valgfri)
                  </label>
                  <input
                    type="number"
                    value={horseForm.age}
                    onChange={(e) => setHorseForm({ ...horseForm, age: e.target.value })}
                    placeholder="F.eks. 8"
                    min="0"
                    max="50"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                {/* Father - Optional */}
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, color: '#333' }}>
                    Far (valgfri)
                  </label>
                  <input
                    type="text"
                    value={horseForm.father}
                    onChange={(e) => setHorseForm({ ...horseForm, father: e.target.value })}
                    placeholder="F.eks. Storm"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                {/* Mother - Optional */}
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, color: '#333' }}>
                    Mor (valgfri)
                  </label>
                  <input
                    type="text"
                    value={horseForm.mother}
                    onChange={(e) => setHorseForm({ ...horseForm, mother: e.target.value })}
                    placeholder="F.eks. Luna"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                  />
                </div>
              </div>

              {/* Buttons */}
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem' }}>
                <button
                  onClick={closeModal}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: '#f5f5f5',
                    color: '#666',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 500
                  }}
                >
                  Annuller
                </button>
                <button
                  onClick={saveHorse}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: '#6b5e8e',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 500
                  }}
                >
                  {editingHorse ? 'Gem ændringer' : 'Tilføj hest'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerNotesPage;
